import { fromJS, OrderedMap } from 'immutable';
import each from 'lodash/each';
import getIn from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import * as Constants from '../../constants';
import { ApiActions } from '../../constants/ApiActions';
import { GraphQL } from '../../graphql';
import { Map } from '../../lib/ImmutableUtils';
import { createReducer } from '../../lib/ReducerUtils';
import { type CatalogSetCurrentSourceIdAction, type IMapOf } from '../../types';

export const INITIAL_STATE = Map({
  [Constants.Catalog.CatalogKeys.DETAILS]: OrderedMap({}) as IMapOf<$TSFixMe>,
  [Constants.Catalog.CatalogKeys.ANNOTATIONS]: OrderedMap({}) as IMapOf<$TSFixMe>,
  [Constants.Catalog.CatalogKeys.CURRENT_SOURCE_ID]: null as string | null, // $TSFixMe
});
export type EntitiesCatalogState = typeof INITIAL_STATE;

export default createReducer(INITIAL_STATE, {
  [ApiActions.CATALOG_GET_DETAILS_SUCCESS]: getDetailsSuccess,
  [ApiActions.CATALOG_GET_ARTIST_DISCOGRAPHY_SUCCESS]: getArtistDiscographySuccess,
  [ApiActions.CATALOG_GET_ALL_ARTIST_TRACKS_SUCCESS]: getAllArtistTracksSuccess,
  [ApiActions.CATALOG_GET_DETAILS_WITH_COLLABORATIONS_SUCCESS]: getDetailsSuccess,
  [ApiActions.CATALOG_GET_ARTIST_DISCOGRAPHY_WITH_COLLABORATIONS_SUCCESS]:
    getArtistDiscographySuccess,
  [ApiActions.CATALOG_GET_ALL_ARTIST_TRACKS_WITH_COLLABORATIONS_SUCCESS]: getAllArtistTracksSuccess,
  [ApiActions.CATALOG_ANNOTATE_OBJECTS_SIMPLE_SUCCESS]: annotateObjects,
  [ApiActions.CATALOG_ANNOTATE_OBJECTS_SUCCESS]: annotateObjects,
  [Constants.Catalog.CatalogActions.CATALOG_SET_CURRENT_SOURCE_ID]: setCurrentSourceId,
  [ApiActions.COLLECTIONS_GET_ITEMS_BY_ARTIST_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_V6_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.COLLECTIONS_GET_SORTED_PLAYLISTS_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.PLAYLISTS_GET_TRACKS_SUCCESS]: savePlaylistAnnotations,
  [ApiActions.PLAYLISTS_SET_DETAILS_SUCCESS]: savePlaylistAnnotations,
  [ApiActions.PLAYLISTS_EDIT_TRACKS_SUCCESS]: savePlaylistAnnotations,
  [Constants.Catalog.CatalogActions.CATALOG_SET_PLAYLISTS]: setPlaylistAnnotations,
  [ApiActions.PODS_GET_PLAYLIST_SEARCH_RECOMMENDATIONS_SUCCESS]:
    savePlaylistRecommendationsAnnotations,
  [ApiActions.SOD_SEARCH_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.GET_FRAGMENT_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.GET_REPLAY_TRACK_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.GET_ARTIST_EVENTS_SUCCESS]: saveArtistEvents,
  [ApiActions.PLAYBACK_SOURCE_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.PLAYBACK_CURRENT_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.PLAYBACK_PEEK_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.AESOP_GET_DETAILS_SUCCESS]: getAesopDetailsSuccess,
  [ApiActions.AESOP_ANNOTATE_OBJECTS_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.AESOP_GET_ALL_THUMBED_EPISODES_BY_PROGRAM_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.AESOP_GET_ALL_EPISODES_BY_PROGRAM_SUCCESS]: addAnnotationsFromEpisodes,
  [ApiActions.PLS_GET_DIRECTORY_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.INTERACTIVE_RADIO_GET_MODES_SUCCESS]: addAnnotationsIfPresent,
  [ApiActions.INTERACTIVE_RADIO_SET_MODE_SUCCESS]: addAnnotationsIfPresent,
  [GraphQL.Constants.OperationNameActions.GRAPHQL_GET_ARTIST_DETAILS_SUCCESS]:
    addDetailsAndAnnotationsFromGraphQL,
  [GraphQL.Constants.OperationNameActions.GRAPHQL_GET_ARTIST_DETAILS_WITH_COLLABORATIONS_SUCCESS]:
    addDetailsAndAnnotationsFromGraphQL,
  [GraphQL.Constants.OperationNameActions.GRAPHQL_GET_ARTIST_COLLABORATIONS_SUCCESS]:
    addDetailsAndAnnotationsFromGraphQL,
});

function addDetailsAndAnnotationsFromGraphQL(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  const details = getIn(action, 'response.entities.details', {});
  const pandoraId = getIn(action, 'response.result.data.entity', null);
  const annotations = getIn(action, 'response.entities.annotations', {});
  const events = getIn(details[pandoraId], 'events', []);
  if (!details[pandoraId]) return state;

  let newState = state;

  if (isArray(events) && events.length) {
    newState = state.setIn([Constants.Catalog.CatalogKeys.EVENTS, pandoraId], fromJS(events));
  }

  return newState
    .mergeIn([Constants.Catalog.CatalogKeys.DETAILS, pandoraId], fromJS(details[pandoraId]))
    .mergeDeep({ [Constants.Catalog.CatalogKeys.ANNOTATIONS]: annotations });
}

function setDetailsAndAnnotationFromResponse(
  state: EntitiesCatalogState,
  response: $TSFixMe,
  pandoraId: string,
): EntitiesCatalogState {
  if (response === null) return state;
  let newState = null;
  const { annotations, ...restOfResponse } = response;

  // In addition to `annotations`, response can have any of:
  // trackDetails, albumDetails, artistDetails, or composerDetails
  newState = state.withMutations(substate => {
    each(restOfResponse, responseData => {
      if (responseData) {
        substate.setIn([Constants.Catalog.CatalogKeys.DETAILS, pandoraId], fromJS(responseData));
      }
    });
  });

  return (newState || state).mergeIn(
    [Constants.Catalog.CatalogKeys.ANNOTATIONS],
    fromJS(annotations),
  );
}

function getDetailsSuccess(state: EntitiesCatalogState, action: $TSFixMe): EntitiesCatalogState {
  const { pandoraId } = action.updateEntitiesWith;
  const response = action.response;
  return setDetailsAndAnnotationFromResponse(state, response, pandoraId);
}

/**
 * Function used by external callers merging into Catalog details, notably ApiDataMapper
 *
 * @param {object} state - original state
 * @param {[object]} data - data to merge in
 * @returns {object} mutated state
 */
export function mergeCatalogDetails(
  state: EntitiesCatalogState,
  data: $TSFixMe,
): EntitiesCatalogState {
  let newState = state;
  const details =
    data && (data.trackDetails || data.albumDetails || data.artistDetails || data.composerDetails);
  if (details) {
    const pandoraId = details.pandoraId;

    // TODO WEBCA-744: revert this once backend resolves. This is a quick fix to ensure incorrect
    // annotations do not display temporarily while graphql fetches the correct annotations
    if (data.artistDetails) {
      const artistDetails = omit(details, ['latestRelease', 'topTracks', 'topAlbums']);
      newState = newState.setIn(
        [Constants.Catalog.CatalogKeys.CATALOG, Constants.Catalog.CatalogKeys.DETAILS, pandoraId],
        fromJS(artistDetails),
      );
    } else {
      newState = newState.setIn(
        [Constants.Catalog.CatalogKeys.CATALOG, Constants.Catalog.CatalogKeys.DETAILS, pandoraId],
        fromJS(details),
      );
    }
  }

  const annotations = data && data.annotations;
  if (annotations) {
    newState = mergeCatalogAnnotation(newState, annotations);
  }
  return newState;
}

function getArtistDiscographySuccess(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  const { response, updateEntitiesWith } = action;
  const { pandoraId } = updateEntitiesWith;
  const { discography, annotations } = response;
  const newState = state.setIn(
    [Constants.Catalog.CatalogKeys.DISCOGRAPHY, pandoraId],
    fromJS(discography),
  );
  return newState.mergeIn([Constants.Catalog.CatalogKeys.ANNOTATIONS], fromJS(annotations));
}

function getAllArtistTracksSuccess(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  const { response, updateEntitiesWith } = action;
  const { pandoraId } = updateEntitiesWith;
  const { tracks, annotations } = response;
  const newState = state.setIn([Constants.Catalog.CatalogKeys.TRACKS, pandoraId], fromJS(tracks));
  return newState.mergeIn([Constants.Catalog.CatalogKeys.ANNOTATIONS], fromJS(annotations));
}

function addAnnotationsIfPresent(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  if (action.ignoreAnnotations) {
    return state;
  }
  let annotations = getIn(action, 'response.result.annotations', null);
  annotations = annotations ? annotations : getIn(action, 'response.annotations', null);
  return state.mergeIn([Constants.Catalog.CatalogKeys.ANNOTATIONS], fromJS(annotations));
}

function getAesopDetailsSuccess(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  const { pandoraId } = action.updateEntitiesWith;
  const response = getIn(action, 'response.details', null);
  return setDetailsAndAnnotationFromResponse(state, response, pandoraId);
}

function addAnnotationsFromEpisodes(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  return addAnnotationFromProperty(state, action, 'episodes');
}

function addAnnotationFromProperty(
  state: EntitiesCatalogState,
  action: $TSFixMe,
  property = 'result',
): EntitiesCatalogState {
  const annotations = getIn(action, `response.${property}.annotations`, null);
  return annotations
    ? state.mergeIn([Constants.Catalog.CatalogKeys.ANNOTATIONS], fromJS(annotations))
    : state;
}

function annotateObjects(state: EntitiesCatalogState, action: $TSFixMe): EntitiesCatalogState {
  return state.mergeIn([Constants.Catalog.CatalogKeys.ANNOTATIONS], fromJS(action.response));
}

/**
 * Function used by external callers merging into Catalog annotations, notably ApiDataMapper
 *
 * @param {object} state - original state
 * @param {[object]} data - data to merge in
 * @returns {object} mutated state
 */
export function mergeCatalogAnnotation(
  state: EntitiesCatalogState,
  data: $TSFixMe,
): EntitiesCatalogState {
  if (!data) {
    return state;
  }
  return state.mergeIn(
    [Constants.Catalog.CatalogKeys.CATALOG, Constants.Catalog.CatalogKeys.ANNOTATIONS],
    fromJS(data),
  );
}

function setCurrentSourceId(
  state: EntitiesCatalogState,
  { payload }: CatalogSetCurrentSourceIdAction,
): EntitiesCatalogState {
  return state.set(Constants.Catalog.CatalogKeys.CURRENT_SOURCE_ID, payload.pandoraId);
}

function makePlaylistAnnotationFromEntity(entity: $TSFixMe): EntitiesCatalogState {
  return entity.delete('annotations').delete('tracks').delete('notModified').delete('offset');
}

// playlist APIs return annotations we want to consolidate here
function savePlaylistAnnotations(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  // usually the APIs will have the playlist ID in the response result (e.g. getTracks)
  let pandoraId = getIn(action, 'response.result');
  // playlist setDetails (i.e. to rename playlist) only has the playlist ID in the request
  if (!pandoraId) {
    pandoraId = getIn(action, 'params.content.request.pandoraId');
  }
  let entity = getIn(action, 'response.entities.playlists', {})[pandoraId];
  // Maybe this is an edit tracks action, which has a differemt payload
  if (action.type === 'PLAYLISTS_EDIT_TRACKS_SUCCESS') {
    entity = action.response;
  }
  if (!isEmpty(entity)) {
    // Type isn't always included, but we need it in places
    entity.type = Constants.SourceTypes.PandoraTypes.PLAYLIST;
    const newState = state.mergeIn(
      [Constants.Catalog.CatalogKeys.ANNOTATIONS],
      fromJS(entity.annotations),
    );
    return newState.mergeIn(
      [Constants.Catalog.CatalogKeys.ANNOTATIONS, pandoraId],
      makePlaylistAnnotationFromEntity(fromJS(entity)),
    );
  }
  return state;
}

// Adds playlists into entities.catalog.annotations from entities.playlists
function setPlaylistAnnotations(
  state: EntitiesCatalogState,
  { payload: { playlists } }: $TSFixMe,
): EntitiesCatalogState {
  if (!playlists || !playlists.size) return state;
  let newState = state;
  // @ts-expect-error
  playlists.forEach(playlist => {
    const playlistId = playlist.get('pandoraId');
    if (playlistId) {
      newState = newState.mergeIn(
        [Constants.Catalog.CatalogKeys.ANNOTATIONS, playlistId],
        playlist,
      );
    }
  });
  return newState;
}

// getPlaylistRecommendations returns annotations we want to consolidate here
function savePlaylistRecommendationsAnnotations(
  state: EntitiesCatalogState,
  action: $TSFixMe,
): EntitiesCatalogState {
  const pandoraId = action.params.content.playlistId;
  return state.mergeIn(
    [Constants.Catalog.CatalogKeys.ANNOTATIONS],
    fromJS(action.response.entities.playlists[pandoraId].annotations),
  );
}

function saveArtistEvents(state: EntitiesCatalogState, action: $TSFixMe): EntitiesCatalogState {
  const events = getIn(action, 'response.artistEvents', null);
  if (!events) {
    return state;
  }

  const grouped = groupBy(events, event => {
    return event.artistPandoraId;
  });

  return state.setIn([Constants.Catalog.CatalogKeys.EVENTS], fromJS(grouped));
}
